"use client";

import type { ReactNode } from "react";
import {
  ArrowUpRight,
  Command,
  ChevronLeft,
  ChevronRight,
  Menu,
  PanelLeftClose,
  PanelLeftOpen,
  X,
} from "lucide-react";
import { useId, useState } from "react";
import { Avatar, IconButton } from "../primitives/ui";
import { useTheme } from "../theme/theme-provider";
import { templates, isSimpleTemplate } from "../theme/templates";

export type NavigationItem = {
  id: string;
  label: string;
  icon: ReactNode;
  count?: number;
  disabled?: boolean;
};
export function StudioMark() {
  return (
    <span className="wordmark">
      <span className="brand-symbol">
        <Command size={22} strokeWidth={2.3} />
      </span>
      <span>
        studio<span className="brand-period">.</span>
      </span>
    </span>
  );
}
export function AppShell({
  children,
  items,
  active,
  onNavigate,
  sidebar,
  header,
  footer,
  userName = "서지안",
  userTeam = "",
  organizationSubtitle = "TEAM SPACE",
  organization,
  brand,
  userAvatarSrc,
  mobileNavigation = "bottom",
  sidebarCollapsible,
  defaultSidebarCollapsed = false,
  showBrand = true,
  sidebarCollapsedMode = "hidden",
  collapsedBrand,
  collapsedOrganization,
  collapsedSidebarFooter,
}: {
  children: ReactNode;
  items: NavigationItem[];
  active: string;
  onNavigate: (id: string) => void;
  sidebar?: ReactNode;
  header?: ReactNode;
  footer?: ReactNode;
  userName?: string;
  userTeam?: string;
  organizationSubtitle?: string;
  organization?: (closeNavigation: () => void) => ReactNode;
  brand?: ReactNode;
  userAvatarSrc?: string;
  mobileNavigation?: "bottom" | "drawer";
  sidebarCollapsible?: boolean;
  defaultSidebarCollapsed?: boolean;
  showBrand?: boolean;
  sidebarCollapsedMode?: "hidden" | "icons";
  collapsedBrand?: ReactNode;
  collapsedOrganization?: ReactNode;
  collapsedSidebarFooter?: ReactNode;
}) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultSidebarCollapsed);
  const sidebarId = useId();
  const { template } = useTheme();
  const simple = isSimpleTemplate(template);
  const collapsible =
    sidebarCollapsible ??
    templates.find((item) => item.id === template)?.navigation ===
      "collapsible";
  const navigation = (
    <nav aria-label="주요 메뉴">
      {items.map((item) => (
        <button
          key={item.id}
          className={`nav-item ${active === item.id ? "is-active" : ""}`}
          aria-current={active === item.id ? "page" : undefined}
          disabled={item.disabled}
          onClick={() => {
            onNavigate(item.id);
            setMobileOpen(false);
          }}
        >
          {item.icon}
          <span>{item.label}</span>
          {item.count !== undefined && (
            <span className="nav-count">{item.count}</span>
          )}
        </button>
      ))}
    </nav>
  );
  return (
    <div
      className={`app-shell ${sidebarCollapsedMode === "icons" ? "app-shell--icon-sidebar" : ""} ${mobileNavigation === "drawer" ? "app-shell--drawer-navigation" : ""} template-${template} ${simple ? "template-simple" : ""} ${collapsible && collapsed ? "sidebar-collapsed" : ""}`}
    >
      <nav aria-label="바로가기" style={{ display: "contents" }}>
        <a className="skip-link" href="#studio-content">
          본문으로 이동
        </a>
      </nav>
      <aside
        id={sidebarId}
        className={`sidebar ${mobileOpen ? "is-open" : ""}`}
        aria-label="Studio 탐색"
      >
        {sidebarCollapsedMode === "icons" && (
          <div className="sidebar-rail">
            <div className="sidebar-rail-top">
              {collapsedOrganization ??
                (showBrand
                  ? (collapsedBrand ?? (
                      <span className="brand-symbol" aria-label="Studio">
                        <Command size={20} />
                      </span>
                    ))
                  : null)}
            </div>
            <nav aria-label="주요 메뉴">
              {items.map((item) => (
                <IconButton
                  key={item.id}
                  label={
                    item.count === undefined
                      ? item.label
                      : `${item.label} · ${item.count}개`
                  }
                  className={`sidebar-rail-item ${active === item.id ? "is-active" : ""}`}
                  aria-current={active === item.id ? "page" : undefined}
                  disabled={item.disabled}
                  onClick={() => onNavigate(item.id)}
                >
                  <span aria-hidden="true">{item.icon}</span>
                  {item.count !== undefined && item.count > 0 && (
                    <span className="sidebar-rail-count" aria-hidden="true" />
                  )}
                </IconButton>
              ))}
            </nav>
            <div className="sidebar-rail-footer">
              {collapsedSidebarFooter}
              <span
                role="img"
                aria-label={`${userName}${userTeam ? ` · ${userTeam}` : ""}`}
                title={`${userName}${userTeam ? ` · ${userTeam}` : ""}`}
              >
                <Avatar
                  name={userName}
                  {...(userAvatarSrc ? { src: userAvatarSrc } : {})}
                  decorative
                />
              </span>
            </div>
          </div>
        )}
        <div
          className={`brand-row${!showBrand ? " brand-row--without-brand" : ""}`}
        >
          {showBrand && (brand ?? <StudioMark />)}
          {simple && <div className="simple-shell-actions">{header}</div>}
          <span className="mobile-only">
            <IconButton label="메뉴 닫기" onClick={() => setMobileOpen(false)}>
              <X size={18} />
            </IconButton>
          </span>
        </div>
        {organization ? (
          organization(() => setMobileOpen(false))
        ) : (
          <div className="organization">
            <span className="organization-icon">B</span>
            <span>
              <strong>BLM Studio</strong>
              <small>{organizationSubtitle}</small>
            </span>
            <ArrowUpRight size={14} />
          </div>
        )}
        {navigation}
        <div className="sidebar-extra">{sidebar}</div>
        <div className="sidebar-footer">
          {footer}
          <div className="user-line">
            <Avatar
              name={userName}
              {...(userAvatarSrc ? { src: userAvatarSrc } : {})}
              decorative
            />
            <span>
              <strong>{userName}</strong>
              {userTeam && <small>{userTeam}</small>}
            </span>
            <span className="online-dot" role="img" aria-label="접속 중" />
          </div>
        </div>
      </aside>
      {collapsible && sidebarCollapsedMode === "icons" && (
        <div className="sidebar-boundary-control">
          <IconButton
            className="sidebar-boundary-toggle"
            label={collapsed ? "메뉴 이름 펼치기" : "메뉴 이름 접기"}
            aria-expanded={!collapsed}
            aria-controls={sidebarId}
            onClick={() => setCollapsed((value) => !value)}
          >
            {collapsed ? (
              <ChevronRight size={16} aria-hidden="true" />
            ) : (
              <ChevronLeft size={16} aria-hidden="true" />
            )}
          </IconButton>
        </div>
      )}
      {mobileOpen && (
        <button
          className="mobile-scrim"
          aria-label="메뉴 닫기"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <div className="workspace">
        <header className="workspace-header">
          {collapsible && sidebarCollapsedMode !== "icons" && (
            <span className="desktop-only">
              <IconButton
                label={collapsed ? "탐색 메뉴 펼치기" : "탐색 메뉴 접기"}
                aria-expanded={!collapsed}
                aria-controls={sidebarId}
                onClick={() => setCollapsed((value) => !value)}
              >
                {collapsed ? (
                  <PanelLeftOpen size={18} />
                ) : (
                  <PanelLeftClose size={18} />
                )}
              </IconButton>
            </span>
          )}
          <div className="mobile-only">
            <IconButton label="메뉴 열기" onClick={() => setMobileOpen(true)}>
              <Menu size={20} />
            </IconButton>
          </div>
          {!simple && header}
        </header>
        <main id="studio-content" className="workspace-content">
          {children}
        </main>
      </div>
      {mobileNavigation === "bottom" && (
        <div className="mobile-bottom">{navigation}</div>
      )}
    </div>
  );
}
